import React from 'react';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import { DATA_LAYERS } from '../../../lib/dataLayers';
import { useStandDensity } from '../../../hooks/useStandDensity';
import { SD_PALETTE } from '../../../lib/sdColors';
import {
  CHART_TOOLTIP_STYLE,
  CHART_TOOLTIP_LABEL_STYLE,
  CHART_TOOLTIP_ITEM_STYLE,
  CHART_AXIS_STYLE,
  CHART_GRID_STROKE,
  CHART_AXIS_STROKE,
  CHART_PRIMARY_COLOR,
} from '../../../lib/chartStyles';
import { LayerHeader } from './LayerHeader';
import { StatCard } from './StatCard';
import { AboutLayer } from './AboutLayer';
import { GlossaryTerm } from '../../shared/GlossaryTerm';
import {
  LayerLoadingLabel,
  LayerErrorAlert,
  LayerLiveBadge,
  SkeletonPulseBlock,
} from './contentPrimitives';

interface StandDensityContentProps {
  millCode: string | null;
  driveTimeMinutes: number;
}

const fmtAcres = (n: number) =>
  n >= 1000 ? `${Math.round(n).toLocaleString()} ac` : `${n.toFixed(1)} ac`;
const fmtPct = (f: number) => `${(f * 100).toFixed(1)}%`;
const fmtRd = (rd: number) => rd.toFixed(2);

const Skeleton: React.FC = () => (
  <div className="space-y-4">
    <LayerLoadingLabel layer={DATA_LAYERS.stand_density} />
    <SkeletonPulseBlock className="h-[240px]" />
    <SkeletonPulseBlock className="h-[120px]" />
  </div>
);

export const StandDensityContent: React.FC<StandDensityContentProps> = ({
  millCode,
  driveTimeMinutes,
}) => {
  const layer = DATA_LAYERS.stand_density;
  const { data, isLoading, error } = useStandDensity(millCode, driveTimeMinutes);

  return (
    <div className="space-y-6">
      <LayerHeader layer={layer} />

      <AboutLayer>
        <p>
          <GlossaryTerm term="rd">Relative Density (RD)</GlossaryTerm> compares each
          forested 30&thinsp;m pixel&rsquo;s current stocking to the biological maximum the
          site can carry &mdash; a self-thinning ceiling defined by Reineke (1933) and
          Long (1985). RD = 0 is open ground; RD = 1 is the mortality threshold
          where competition starts killing trees.
        </p>
        <p>
          Bars show the share of <em>forested</em> acres in each 30-percentage-point
          bin. <strong>0&ndash;30%</strong> stands are understocked (room to grow);
          <strong> 30&ndash;60%</strong> are growing healthily; <strong>60&ndash;80%</strong>
          are approaching full stocking; <strong>80%+</strong> are overstocked &mdash;
          thinning candidates with elevated competition stress and crown-fire fuel
          continuity. Non-forest area (water, developed, ag, bare) is reported
          separately as a fraction of the full shed.
        </p>
        <p>
          The trend line tracks shed-wide mean RD across three snapshots. A rising
          line is densification &mdash; common across western US forests after a
          century of fire suppression, and a leading indicator for fire and insect
          vulnerability.
        </p>
      </AboutLayer>

      {isLoading && <Skeleton />}

      {!isLoading && error && (
        <LayerErrorAlert layer={layer} details={error} />
      )}

      {!isLoading && data && data.latest.forestAcres > 0 && (() => {
        const latest = data.latest;
        const totalShed = latest.forestAcres + latest.nonForestAcres;
        const nonForestFrac = totalShed > 0 ? latest.nonForestAcres / totalShed : 0;

        const chartData = SD_PALETTE.order.map(bin => ({
          bin,
          label: SD_PALETTE.labels[bin],
          acres: latest.binAcres[bin],
          percent: latest.forestAcres > 0 ? (latest.binAcres[bin] / latest.forestAcres) * 100 : 0,
        }));

        const trendData = data.years.map(y => ({ year: y.year, meanRd: y.meanRd }));
        const first = data.years[0];
        const delta = latest.meanRd - first.meanRd;
        const deltaSign = delta >= 0 ? '+' : '';
        const deltaColor = delta >= 0 ? 'text-red-400' : 'text-green-400';
        const overstockedPct = chartData[3].percent;
        const understockedPct = chartData[0].percent;

        return (
          <>
            <LayerLiveBadge />

            <div className="bg-figma-card rounded-figma-panel p-4 border border-figma-lime/30 shadow-figma-card">
              <h4 className="text-base font-roboto font-bold text-figma-text mb-1">
                Distribution by RD class <span className="text-neutral-500 font-normal">({latest.year})</span>
              </h4>
              <p className="text-xs font-roboto text-neutral-500 mb-3">
                Share of forested acres in each relative density bin
              </p>
              <ResponsiveContainer width="100%" height={240} minWidth={0}>
                <BarChart
                  data={chartData}
                  layout="vertical"
                  margin={{ top: 5, right: 16, left: 8, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} horizontal={false} />
                  <XAxis
                    type="number"
                    domain={[0, 100]}
                    stroke={CHART_AXIS_STROKE}
                    style={CHART_AXIS_STYLE}
                    tickFormatter={(v: number) => `${v}%`}
                  />
                  <YAxis
                    type="category"
                    dataKey="label"
                    stroke={CHART_AXIS_STROKE}
                    style={CHART_AXIS_STYLE}
                    width={80}
                  />
                  <Tooltip
                    cursor={false}
                    contentStyle={CHART_TOOLTIP_STYLE}
                    labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                    itemStyle={CHART_TOOLTIP_ITEM_STYLE}
                    formatter={(value: number | undefined, _name: string | undefined, props: { payload?: { acres?: number } }) => {
                      const pct = value ?? 0;
                      const acres = props.payload?.acres ?? 0;
                      return [`${pct.toFixed(1)}%  (${fmtAcres(acres)})`, 'Share of forested'];
                    }}
                  />
                  <Bar dataKey="percent" isAnimationActive={false}>
                    {chartData.map(d => (
                      <Cell key={d.bin} fill={SD_PALETTE.colors[d.bin]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="flex items-center justify-between pt-3 mt-2 border-t border-figma-lime/20 text-sm font-roboto">
                <span className="text-neutral-400">Non-forest area</span>
                <span className="font-roboto-mono text-neutral-300">
                  {fmtPct(nonForestFrac)}
                  <span className="text-neutral-500 ml-2">of shed</span>
                </span>
              </div>
              <p className="text-xs font-roboto italic text-neutral-500 mt-2 leading-relaxed">
                Bars and map colors share the same 4-class palette &mdash; a red pixel
                on the map is in the same overstocked bucket the 80%+ bar counts.
              </p>
            </div>

            {data.years.length > 1 && (
              <div className="bg-figma-card rounded-figma-panel p-4 border border-figma-lime/30 shadow-figma-card">
                <h4 className="text-base font-roboto font-bold text-figma-text mb-1">
                  Mean RD trend <span className="text-neutral-500 font-normal">({first.year}&ndash;{latest.year})</span>
                </h4>
                <p className="text-xs font-roboto text-neutral-500 mb-3">
                  Area-weighted mean RD across forested pixels
                </p>
                <ResponsiveContainer width="100%" height={140} minWidth={0}>
                  <LineChart data={trendData} margin={{ top: 5, right: 12, left: 8, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} />
                    <XAxis
                      dataKey="year"
                      stroke={CHART_AXIS_STROKE}
                      style={CHART_AXIS_STYLE}
                      tickMargin={6}
                    />
                    <YAxis
                      stroke={CHART_AXIS_STROKE}
                      style={CHART_AXIS_STYLE}
                      domain={[0, 1]}
                      tickFormatter={(v: number) => v.toFixed(1)}
                      width={36}
                    />
                    <Tooltip
                      contentStyle={CHART_TOOLTIP_STYLE}
                      formatter={(value: number | undefined) => [fmtRd(value ?? 0), 'Mean RD']}
                      labelFormatter={(label) => `Year ${label}`}
                    />
                    <Line
                      type="monotone"
                      dataKey="meanRd"
                      stroke={CHART_PRIMARY_COLOR}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
                <div className={`flex items-center gap-2 mt-2 text-sm font-roboto ${deltaColor}`}>
                  <span className="font-roboto-mono font-bold">
                    {deltaSign}{delta.toFixed(2)} RD
                  </span>
                  <span className="text-neutral-500">
                    over {latest.year - first.year} years &mdash; {delta >= 0 ? 'densifying' : 'thinning'}
                  </span>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <StatCard label={`Mean RD (${latest.year})`} value={fmtRd(latest.meanRd)} />
              <StatCard label="Forested area" value={fmtAcres(latest.forestAcres)} />
              <StatCard label="Overstocked (80%+)" value={`${overstockedPct.toFixed(1)}%`} />
              <StatCard label={'Understocked (0\u201330%)'} value={`${understockedPct.toFixed(1)}%`} />
            </div>
          </>
        );
      })()}
    </div>
  );
};
